import { useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { homeNavItem, navGroups } from './navConfig';
import useStoreSetupStatus from '../hooks/useStoreSetupStatus';
import { MyContext } from '../context';

const linkClass = ({ isActive }) =>
	`text-sm rounded-lg flex items-center py-2 pl-9 pr-2 group ${
		isActive ? 'bg-gray-100 text-black font-medium' : 'text-gray-700 hover:bg-gray-50'
	}`;

const topLevelLinkClass = ({ isActive }) =>
	`text-base font-normal rounded-lg hover:bg-gray-100 flex items-center p-2 group ${
		isActive ? 'bg-gray-100 text-black font-medium' : 'text-gray-900'
	}`;

function NavFlatLink({ group }) {
	const Icon = group.icon;

	return (
		<li>
			<NavLink
				to={group.to}
				end
				className={({ isActive }) =>
					`text-sm text-gray-900 font-medium rounded-lg hover:bg-gray-100 flex items-center p-2 ${
						isActive ? 'bg-gray-100 text-black' : ''
					}`
				}
			>
				<Icon className='size-5' />
				<span className='ml-3'>{group.label}</span>
			</NavLink>
		</li>
	);
}

function NavGroup({ group }) {
	const [open, setOpen] = useState(false);
	const Icon = group.icon;

	if (!group.items) return <NavFlatLink group={group} />;

	return (
		<li>
			<button
				type='button'
				onClick={() => setOpen(!open)}
				className='w-full text-sm text-gray-900 font-medium rounded-lg hover:bg-gray-100 flex items-center justify-between p-2'
			>
				<span className='flex items-center'>
					<Icon className='size-5' />
					<span className='ml-3'>{group.label}</span>
				</span>
				<ChevronDown className={`size-4 text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`} />
			</button>
			{open && (
				<ul className='space-y-1 pb-2'>
					{group.items.map((item) => (
						<li key={item.to}>
							<NavLink to={item.to} className={linkClass} end>
								{item.label}
							</NavLink>
						</li>
					))}
				</ul>
			)}
		</li>
	);
}

// Role is per-store now except PLATFORMADMIN, which stays global -- callers
// pass the caller's flat userProfile.role plus the resolved role for
// whichever store is currently active (see context.js's activeStoreRole).
// canApprove gates items flagged `requiresApprover` (e.g. Workflows >
// Approvals) -- that's an orthogonal permission from role, so it's not
// expressible via `roles` alone.
function visibleGroupsForRole(role, storeRole, canApprove) {
	const effectiveRole = role === 'PLATFORMADMIN' ? 'PLATFORMADMIN' : storeRole;
	return navGroups
		.map((group) => ({
			...group,
			items: group.items
				?.filter((item) => !item.roles || item.roles.includes(effectiveRole))
				.filter((item) => !item.requiresApprover || canApprove),
		}))
		.filter((group) => !group.items || group.items.length > 0);
}

function Navigation() {
	const HomeIcon = homeNavItem.icon;
	const navigate = useNavigate();
	const { userProfile, activeStoreRole, activeStoreIsOrderApprover } = useContext(MyContext);
	const { loading, allDone, refresh } = useStoreSetupStatus();
	const isStoreAdmin = userProfile?.role === 'PLATFORMADMIN' || activeStoreRole === 'ADMIN';
	// Gates nav items flagged `requiresApprover` (e.g. Workflows > Approvals)
	// for admins/approvers. Order approvals themselves are the "Needs
	// approval" filter on /orders, not a nav item.
	const canApprove = isStoreAdmin || activeStoreIsOrderApprover;
	const groups = visibleGroupsForRole(userProfile?.role, activeStoreRole, canApprove);
	// Same "is the active store ready" check the redirect-on-switch guard in
	// layout.js uses -- while incomplete, Home just always points at Finish
	// Setup instead of the dashboard, so it's easy to get back there without
	// waiting for the once-per-switch auto-redirect to fire again.
	//
	// This hook only re-fetches when the active store itself changes, so its
	// `allDone` can go stale the moment a setup task is completed elsewhere
	// without switching stores (e.g. saving Payments, then clicking Home) --
	// clicking Home re-checks live rather than trusting that cached value.
	const homeTo = !loading && !allDone ? '/settings/setup' : homeNavItem.to;
	const handleHomeClick = async (e) => {
		e.preventDefault();
		const fresh = await refresh();
		navigate(fresh.allDone ? homeNavItem.to : '/settings/setup');
	};

	return (
		<aside
			id='sidebar'
			className='fixed hidden z-20 h-full top-0 left-0 pt-16 flex lg:flex flex-shrink-0 flex-col w-64 transition-width duration-75'
			aria-label='Sidebar'
		>
			<div className='relative flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white pt-0 overflow-y-auto'>
				<div className='flex-1 flex flex-col pt-5 pb-4'>
					<div className='px-3 bg-white divide-y space-y-1'>
						<ul className='space-y-1 pb-2'>
							<li>
								<NavLink to={homeTo} onClick={handleHomeClick} className={topLevelLinkClass} end>
									<HomeIcon className='size-6' />
									<span className='ml-3'>{homeNavItem.label}</span>
								</NavLink>
							</li>
						</ul>
						<ul className='space-y-1 pt-2'>
							{groups.map((group) => (
								<NavGroup key={group.id} group={group} />
							))}
						</ul>
					</div>
				</div>
			</div>
		</aside>
	);
}

export default Navigation;
